import { v4 as uuidv4 } from "uuid";

import { createAnalyticsStatsigClient } from "./analytics-statsig-client";

import type { AnalyticsStatsigClient } from "./analytics-statsig-client";
import type {
  AnalyticsEventDeviceParams,
  AnalyticsEventUserParams,
  StatsigMetadataV2,
  StatsigUser,
} from "./gen/client-events/index.protobuf_analytics_events.v1";
import type {
  AnalyticsBrowser,
  AnalyticsBrowserSettings,
  InitOptions,
  UserTraits,
} from "@segment/analytics-next";
import type {
  Statsig as StatsigServer,
  StatsigUser as StatsigServerUser,
} from "@statsig/statsig-node-core";

export type ProtoEnum = Record<string, string>;

const STATSIG_SDK_TYPE = "javascript-client";
// Keep this in sync with the exact @oai-statsig/js-client dependency in
// package.json. The test compares this metadata against the SDK export.
const STATSIG_SDK_VERSION = "3.32.7";
const MAX_PENDING_STRUCTURED_TRACK_MS = 30_000;
const PROTOBUF_STRUCTURED_EVENT_NAME = "__protobuf_structured_event__";
const URL_WITH_PROTOCOL_REGEX = /^[a-zA-Z][a-zA-Z\d+.-]*:\/\//;

function isLocalClientEventsServiceHostname(hostname: string): boolean {
  const normalizedHostname = hostname.toLowerCase();
  return (
    normalizedHostname === "localhost" ||
    normalizedHostname === "127.0.0.1" ||
    normalizedHostname === "::1" ||
    normalizedHostname === "[::1]"
  );
}

// Mirrors the minimal structure emitted by ts-proto so we can avoid importing
// MessageFns, which is no longer exported when exportCommonSymbols=false.
export type MessageFns<T, TypeName extends string> = {
  readonly $type: TypeName;
  readonly __payloadType?: T; // phantom, not emitted
};

// Infer the payload type carried by a generated MessageFns helper.
export type MessagePayload<M extends MessageFns<any, string>> =
  M extends MessageFns<infer P, string> ? P : never;

// Compile-time guard that forbids properties outside the declared message payload.
export type NoExtraKeys<TActual, TShape> =
  Exclude<keyof TActual, keyof TShape> extends never ? TActual : never;

export type IdentifiedUser = {
  userId: string;

  // Traits are pieces of information about the user that you want to log in the identify call.
  // WARNING: Please DO NOT include PII here.
  traits: UserTraits;
};

export type AnalyticsLoggerInstrumentation = {
  getPunchOut: () => string | undefined;
  count: (metric_name: string, tags: Record<string, string>) => void;
  addError: (error: Error, context?: object) => void;
  addAction: (action_name: string, context?: object) => void;
};

type StatsigClientContextForAnalyticsLogger = {
  options?: {
    environment?: {
      tier?: unknown;
    };
  };
  stableID?: string | null;
  session?: {
    data?: {
      sessionID?: string;
    };
  };
  user?: {
    userID?: string;
    customIDs?: Record<string, unknown>;
    email?: string;
    ip?: string;
    userAgent?: string;
    country?: string;
    locale?: string;
    appVersion?: string;
    custom?: Record<string, unknown>;
  };
};

type StatsigClientEventForAnalyticsLogger = {
  eventName: string;
  metadata?: Record<string, string>;
};

type StatsigClientLogEventForAnalyticsLogger = (
  eventOrName: StatsigClientEventForAnalyticsLogger | string,
  value?: string | number,
  metadata?: Record<string, string>,
) => unknown;

// Keep this boundary structural so callers using either the public or private
// browser SDK can pass the narrow client surface that AnalyticsLogger needs.
export type StatsigClientForAnalyticsLogger = {
  getContext: () => StatsigClientContextForAnalyticsLogger;
  logEvent?: StatsigClientLogEventForAnalyticsLogger;
  flush?: () => unknown;
  networkConfig?: {
    logEventUrl?: string | null;
  };
};

export type StatsigServerClientForAnalyticsLogger = Pick<
  StatsigServer,
  "flushEvents" | "logEvent"
> & {
  options?: {
    logEventUrl?: string | null;
  };
};

export type StructuredEventOptions = {
  // statsig-node-core requires a StatsigUser on each logEvent call.
  user?: StatsigServerUser;
};

export type StructuredEventIdentifier = string | MessageFns<any, string>;

declare const structuredEventHandlerIntrospection: unique symbol;

type StructuredEventHandlerIntrospection = {
  readonly [structuredEventHandlerIntrospection]: true;
};

export type StructuredEventHandler<Result, ExtraArguments extends unknown[] = []> = {
  <M extends MessageFns<any, string>, Payload extends MessagePayload<M>>(
    messageType: M,
    payload: NoInfer<NoExtraKeys<Payload, MessagePayload<M>>>,
    ...extraArguments: ExtraArguments
  ): Result;
  // Parameters<T> selects the final overload. Keep that representation useful
  // for test spies without exposing a broad call signature.
  (
    this: StructuredEventHandlerIntrospection,
    messageType: MessageFns<any, string>,
    payload: any,
    ...extraArguments: ExtraArguments
  ): Result;
};

export const defaultInstrumentation: AnalyticsLoggerInstrumentation = {
  getPunchOut: () => undefined,
  count: (_metric_name, _tags) => {},
  addError: (_error, _context) => {},
  addAction: (_action_name, _context) => {},
};

type AnalyticsBrowserConstructor = new () => AnalyticsBrowser;

type SegmentAnalyticsNamespace = {
  readonly AnalyticsBrowser?: unknown;
  readonly default?: unknown;
};

const isAnalyticsBrowserConstructor = (value: unknown): value is AnalyticsBrowserConstructor =>
  typeof value === "function";

export function getAnalyticsBrowserConstructor(mod: unknown): AnalyticsBrowserConstructor | null {
  if (mod == null || typeof mod !== "object") {
    return null;
  }

  const namespace = mod as SegmentAnalyticsNamespace;
  if (isAnalyticsBrowserConstructor(namespace.AnalyticsBrowser)) {
    return namespace.AnalyticsBrowser;
  }

  const defaultExport = namespace.default;
  if (defaultExport == null || typeof defaultExport !== "object") {
    return null;
  }

  const defaultNamespace = defaultExport as SegmentAnalyticsNamespace;
  return isAnalyticsBrowserConstructor(defaultNamespace.AnalyticsBrowser)
    ? defaultNamespace.AnalyticsBrowser
    : null;
}

export enum AuthStatus {
  LoggedOut = "logged_out",
  LoggedIn = "logged_in",
}

type Deferred<T> = {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
};

type StructuredEventIdentifiers = {
  eventCreatedAt: string;
  eventId: string;
};

type StructuredEventTransport = "segment" | "statsig";

type SegmentIoIntegrationSettings = {
  readonly apiHost?: unknown;
  readonly protocol?: unknown;
};

function isDevelopmentOrStagingStatsigEnvironmentTier(tier: unknown): boolean {
  return tier === "development" || tier === "dev" || tier === "staging";
}

function createDeferred<T>(): Deferred<T> {
  let resolve!: Deferred<T>["resolve"];
  let reject!: Deferred<T>["reject"];
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });

  return { promise, resolve, reject };
}

export class AnalyticsLogger {
  // It might seem weird that we put the AnalyticsBrowser in an Array.
  // This is because the AnalyticsBrowser implements PromiseLike<[Analytics, Context]>,
  // which means that returning an AnalyticsBrowser directly from a Promise is
  // not possible due to promise flattening.
  private analytics: Promise<[AnalyticsBrowser]>;
  private appName: string;
  private appVersion: string;

  /**
   * A uuidv4 value that's fetched from the oai-did cookie on the .openai.com domain.
   * Please read or set this cookie in every app so we can share the same value.
   *
   * This is how we set it in web: https://github.com/openai/openai/blob/master/chatgpt/web/utils/device-cookie.ts
   */
  private deviceId: string;
  private browserLocale: string;
  private options: InitOptions;
  private settings: AnalyticsBrowserSettings;
  private user?: IdentifiedUser;
  private analyticsStatsigClient?: AnalyticsStatsigClient;
  private instrumentation: AnalyticsLoggerInstrumentation;
  private statscTags: Record<string, string>;
  private initializePromise?: Promise<void>;
  private readonly initializeResolvedGate = createDeferred<void>();
  private structuredEventTransport?: StructuredEventTransport;
  private pendingStructuredTrackPromises = new Set<Promise<unknown>>();

  constructor({
    appName,
    appVersion,
    deviceId,
    browserLocale,
    options,
    settings,
    instrumentation = defaultInstrumentation,
  }: {
    appName: string;
    appVersion: string;
    deviceId: string;
    browserLocale: string;
    options: InitOptions;
    settings: AnalyticsBrowserSettings;
    instrumentation?: AnalyticsLoggerInstrumentation;
  }) {
    this.appName = appName;
    this.appVersion = appVersion;
    this.instrumentation = instrumentation;
    this.statscTags = {
      app_name: this.appName,
      app_version: this.appVersion,
    };
    const recordSegmentImportFailure = () => {
      this.instrumentation.count("AnalyticsLogger.segmentImport.failed", this.statscTags);
    };
    this.analytics = new Promise((resolve) => {
      import("@segment/analytics-next")
        .then((mod) => {
          const AnalyticsBrowserConstructor = getAnalyticsBrowserConstructor(mod);
          if (!AnalyticsBrowserConstructor) {
            recordSegmentImportFailure();
            // Don't resolve the promise. `await this.analytics` will never resolve.
            return;
          }
          const analytics = new AnalyticsBrowserConstructor();
          resolve([analytics]);
        })
        .catch(() => {
          recordSegmentImportFailure();
          // Don't resolve the promise. `await this.analytics` will never resolve.
        });
    });
    this.deviceId = deviceId;
    this.browserLocale = browserLocale;
    this.options = options;
    this.settings = settings;
  }

  /**
   * The `initialize` method loads the analytics sdk.
   *
   * If this method isn't called, none of the other calls to analytics will do anything.
   * Make sure to only call this once.
   */
  async initialize({
    user,
    statsigClient,
    statsigServerClient,
  }: {
    user: IdentifiedUser | undefined;
    statsigClient?: StatsigClientForAnalyticsLogger;
    statsigServerClient?: StatsigServerClientForAnalyticsLogger;
  }) {
    if (statsigClient !== undefined && statsigServerClient !== undefined) {
      throw new Error(
        "AnalyticsLogger.initialize accepts either statsigClient or statsigServerClient, not both.",
      );
    }

    if (user) {
      this.user = user;
    }
    this.analyticsStatsigClient =
      createAnalyticsStatsigClient({
        statsigClient,
        statsigServerClient,
      }) ?? this.analyticsStatsigClient;

    if (!this.initializePromise) {
      this.instrumentation.count("AnalyticsLogger.initialize.start", this.statscTags);
      this.initializePromise = (async () => {
        this.structuredEventTransport = this.selectStructuredEventTransport();

        if (this.structuredEventTransport === "statsig") {
          if (this.analyticsStatsigClient?.sdkType === "client") {
            void this.trackStructuredEventInternal("ChatgptUserIdentified", {});
          }
          this.instrumentation.count("AnalyticsLogger.initialize.success", this.statscTags);
          return;
        }

        const [analytics] = await this.analytics;
        // The catch needs to be chained right after the load call. Otherwise it doesn't catch load errors.
        analytics.load(this.settings, this.options).catch((_: any) => {
          this.instrumentation.count("AnalyticsLogger.initialize.failed", this.statscTags);
        });
        analytics.ready(() => {
          this.instrumentation.count("AnalyticsLogger.initialize.success", this.statscTags);
        });
      })()
        .then(() => {
          this.initializeResolvedGate.resolve();
        })
        .catch((error) => {
          this.initializeResolvedGate.reject(error);
          // When nothing is waiting on the gate, initialize() should be the only
          // reported failure surface.
          void this.initializeResolvedGate.promise.catch(() => undefined);
          throw error;
        });
    }

    await this.initializePromise;
  }

  private getSegmentIoApiHostUrl(): URL | undefined {
    const segmentIntegration = (this.options.integrations as Record<string, unknown> | undefined)?.[
      "Segment.io"
    ];
    if (segmentIntegration == null || typeof segmentIntegration !== "object") {
      return undefined;
    }

    const { apiHost, protocol } = segmentIntegration as SegmentIoIntegrationSettings;
    if (typeof apiHost !== "string" || apiHost.trim().length === 0) {
      return undefined;
    }

    const normalizedApiHost = apiHost.trim();
    const normalizedProtocol =
      typeof protocol === "string" && protocol.trim().length > 0
        ? protocol.trim().replace(/:$/, "")
        : "https";

    try {
      return URL_WITH_PROTOCOL_REGEX.test(normalizedApiHost)
        ? new URL(normalizedApiHost)
        : new URL(`${normalizedProtocol}://${normalizedApiHost}`);
    } catch {
      return undefined;
    }
  }

  private isClientEventsServiceLogEventUrl(logEventUrl: string): boolean {
    const configuredUrl = this.getSegmentIoApiHostUrl();
    if (!configuredUrl) {
      return false;
    }

    let parsedUrl: URL;
    try {
      // Allow callers to pass relative paths such as /ces/v1/rgstr.
      parsedUrl = new URL(logEventUrl, configuredUrl);
    } catch {
      return false;
    }

    const normalizedHost = parsedUrl.host.toLowerCase();
    const normalizedPath = parsedUrl.pathname.replace(/\/+$/, "");
    const configuredHost = configuredUrl.host.toLowerCase();
    const isLocalClientEventsServiceHost = isLocalClientEventsServiceHostname(parsedUrl.hostname);
    const isGatewayClientEventsServicePath =
      normalizedPath === "/ces/v1/rgstr" || normalizedPath === "/ces/v1/log_event";
    const isLocalDirectClientEventsServicePath =
      isLocalClientEventsServiceHost &&
      (normalizedPath === "/v1/rgstr" || normalizedPath === "/v1/log_event");

    return (
      normalizedHost === configuredHost &&
      (isGatewayClientEventsServicePath || isLocalDirectClientEventsServicePath)
    );
  }

  private throwIfRejectedStatsigLogEventUrlOverride(logEventUrl: unknown): void {
    if (typeof logEventUrl !== "string" || logEventUrl.trim().length === 0) {
      return;
    }
    if (this.isClientEventsServiceLogEventUrl(logEventUrl)) {
      return;
    }

    const statsigEnvironmentTier = this.analyticsStatsigClient?.getEnvironmentTier();
    if (!isDevelopmentOrStagingStatsigEnvironmentTier(statsigEnvironmentTier)) {
      return;
    }

    const configuredUrl = this.getSegmentIoApiHostUrl();

    throw new Error(
      [
        "Statsig logEventUrl override was rejected by AnalyticsLogger transport selection.",
        `logEventUrl=${logEventUrl}`,
        `segmentApiHost=${configuredUrl?.host ?? "unknown"}`,
        `statsigEnvironmentTier=${statsigEnvironmentTier ?? "unknown"}`,
        "Expected /ces/v1/rgstr or /ces/v1/log_event, or /v1/rgstr or /v1/log_event for local development.",
      ].join(" "),
    );
  }

  private selectStructuredEventTransport(): StructuredEventTransport {
    const analyticsStatsigClient = this.analyticsStatsigClient;
    if (analyticsStatsigClient === undefined) {
      return "segment";
    }

    if (!analyticsStatsigClient.canLogEvent()) {
      return "segment";
    }

    const logEventUrl = analyticsStatsigClient.getLogEventUrl();
    this.throwIfRejectedStatsigLogEventUrlOverride(logEventUrl);
    // statsig-node-core defaults to Statsig's event endpoint; only use it
    // here when callers provide a CES logEvent endpoint override.
    if (
      typeof logEventUrl === "string" &&
      logEventUrl.trim().length > 0 &&
      this.isClientEventsServiceLogEventUrl(logEventUrl)
    ) {
      return "statsig";
    }
    return "segment";
  }

  // ---------------------------------------------------------------------
  // Structured protobuf analytics events
  // ---------------------------------------------------------------------

  private buildSegmentEnvelope<M extends MessageFns<any, string>>(
    messageType: M,
    msg: MessagePayload<M>,
    identifiers: StructuredEventIdentifiers,
  ) {
    const typeName = messageType.$type;

    const statsigContext = this.analyticsStatsigClient?.getContext();
    const statsigContextUser = statsigContext?.user;
    const statsigStableId = statsigContext?.stableID ?? undefined;

    let statsigMetadataV2: StatsigMetadataV2 | undefined;
    if (statsigContext) {
      const locale = statsigContextUser?.locale ?? this.browserLocale;
      const language = locale ? locale.split("-")[0] : undefined;

      statsigMetadataV2 = {
        stableId: statsigStableId,
        sdkType: STATSIG_SDK_TYPE,
        sdkVersion: STATSIG_SDK_VERSION,
        sessionId: statsigContext?.session?.data?.sessionID,
        appIdentifier: this.appName,
        appVersion: this.appVersion,
        locale,
        language,
      } satisfies StatsigMetadataV2;
    }
    if (statsigContextUser) {
      let statsigCustomIds: Record<string, string> | undefined;
      if (statsigContextUser.customIDs) {
        statsigCustomIds = Object.fromEntries(
          Object.entries(statsigContextUser.customIDs).filter(
            (entry): entry is [string, string] => typeof entry[1] === "string",
          ),
        );
      }

      let statsigCustom: Record<string, string> | undefined;
      if (statsigContextUser.custom) {
        const entries = Object.entries(statsigContextUser.custom).flatMap((entry) => {
          const [key, value] = entry;
          if (value === undefined) {
            return [];
          }

          try {
            const encoded = JSON.stringify(value);
            if (encoded === undefined) {
              return [];
            }
            return [[key, encoded] as [string, string]];
          } catch {
            return [];
          }
        });
        if (entries.length > 0) {
          statsigCustom = Object.fromEntries(entries);
        }
      }

      const statsigUser = {
        userId: statsigContextUser.userID,
        customIds: statsigCustomIds,
        email: statsigContextUser.email,
        ip: statsigContextUser.ip,
        userAgent: statsigContextUser.userAgent,
        country: statsigContextUser.country,
        locale: statsigContextUser.locale,
        appVersion: statsigContextUser.appVersion,
        custom: statsigCustom,
      } satisfies StatsigUser;

      if (statsigMetadataV2) {
        statsigMetadataV2.user = statsigUser;
      }
    }

    const isOpenaiInternal = this.user?.traits?.["is_openai_internal"] as boolean | undefined;

    const userParams = {
      userId: this.user?.userId ?? "",
      deviceId: this.deviceId,
      authStatus: this.user ? AuthStatus.LoggedIn : AuthStatus.LoggedOut,
      planType: this.user?.traits?.["plan_type"] as string | undefined,
      workspaceId: this.user?.traits?.["workspace_id"] as string | undefined,
      isOpenaiInternal,
    } satisfies AnalyticsEventUserParams;
    const deviceParams = {
      // Note: ipAddress is optional on AnalyticsEventDeviceParams and may be
      // populated upstream by ingestion infra; we omit it here.
    } satisfies AnalyticsEventDeviceParams;
    return {
      eventId: identifiers.eventId,
      eventCreatedAt: identifiers.eventCreatedAt,
      eventType: "web",
      userParams,
      deviceParams,
      statsigMetadataV2,
      eventParams: {
        "@type": `openai.buf.dev/openai/protobuf-analytics-events/${typeName}`,
        ...msg,
      },
      punchOutInfoToken: this.instrumentation.getPunchOut(),
      clientMetadata: {
        name: this.appName,
        version: this.appVersion,
      },
    } as const;
  }

  private buildStatsigEnvelope<M extends MessageFns<any, string>>(
    messageType: M,
    msg: MessagePayload<M>,
    identifiers: StructuredEventIdentifiers,
  ) {
    const typeName = messageType.$type;
    const deviceParams = {
      // Note: ipAddress is optional on AnalyticsEventDeviceParams and may be
      // populated upstream by ingestion infra; we omit it here.
    } satisfies AnalyticsEventDeviceParams;
    return {
      eventId: identifiers.eventId,
      eventCreatedAt: identifiers.eventCreatedAt,
      eventType: "web",
      deviceParams,
      eventParams: {
        "@type": `openai.buf.dev/openai/protobuf-analytics-events/${typeName}`,
        ...msg,
      },
      punchOutInfoToken: this.instrumentation.getPunchOut(),
    } as const;
  }

  private normalizeEventName(typeName: string) {
    const pascalName = typeName.split(".").pop() ?? typeName;

    return pascalName
      .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
      .replace(/([A-Z])([A-Z][a-z])/g, "$1_$2")
      .toLowerCase();
  }

  private settleWithin(promise: Promise<unknown>, timeoutMs: number): Promise<void> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<void>((resolve) => {
      timeoutId = setTimeout(resolve, timeoutMs);
    });
    const settledPromise = promise.catch(() => undefined).then(() => undefined);

    return Promise.race([settledPromise, timeoutPromise]).finally(() => {
      if (timeoutId != null) {
        clearTimeout(timeoutId);
      }
    });
  }

  private createStructuredEventIdentifiers(): StructuredEventIdentifiers {
    return {
      eventId: uuidv4(),
      eventCreatedAt: new Date().toISOString(),
    };
  }

  private scheduleStructuredEvent<M extends MessageFns<any, string>>(
    messageType: M,
    buildPayload: () => MessagePayload<M>,
    options?: StructuredEventOptions,
  ): {
    identifiers: StructuredEventIdentifiers;
    trackPromise: Promise<void>;
  };
  private scheduleStructuredEvent(
    messageType: StructuredEventIdentifier,
    buildPayload: () => unknown,
    options?: StructuredEventOptions,
  ): {
    identifiers: StructuredEventIdentifiers;
    trackPromise: Promise<void>;
  };
  private scheduleStructuredEvent(
    messageType: StructuredEventIdentifier,
    buildPayload: () => unknown,
    options?: StructuredEventOptions,
  ) {
    const event =
      typeof messageType === "string"
        ? { $type: `protobuf_analytics_events.v1.${messageType}` }
        : messageType;
    const identifiers = this.createStructuredEventIdentifiers();
    const trackPromise = this.initializeResolvedGate.promise.then(() =>
      this.sendStructuredEvent(event, buildPayload(), identifiers, options),
    );

    // Flush must see this promise immediately, including already-initialized calls before
    // sendStructuredEvent has a chance to register any downstream Segment track promise.
    this.trackStructuredTrackPromise(trackPromise);

    return { identifiers, trackPromise };
  }

  trackStructuredEvent<M extends MessageFns<any, string>, Payload extends MessagePayload<M>>(
    messageType: M,
    payload: NoInfer<NoExtraKeys<Payload, MessagePayload<M>>>,
    options?: StructuredEventOptions,
  ): Promise<void>;
  async trackStructuredEvent(
    messageType: StructuredEventIdentifier,
    payload: unknown,
    options?: StructuredEventOptions,
  ): Promise<void> {
    return this.trackStructuredEventInternal(messageType, payload, options);
  }

  private async trackStructuredEventInternal(
    messageType: StructuredEventIdentifier,
    payload: unknown,
    options?: StructuredEventOptions,
  ): Promise<void> {
    const { trackPromise } = this.scheduleStructuredEvent(messageType, () => payload, options);
    await trackPromise;
  }

  /**
   * Schedules a structured event and returns its envelope ID synchronously so
   * callers can correlate a second transport without generating a competing ID.
   */
  trackStructuredEventAndReturnId<
    M extends MessageFns<any, string>,
    Payload extends MessagePayload<M>,
  >(
    messageType: M,
    payload: NoInfer<NoExtraKeys<Payload, MessagePayload<M>>>,
    options?: StructuredEventOptions,
  ): string | undefined {
    try {
      const { identifiers } = this.scheduleStructuredEvent(messageType, () => payload, options);
      return identifiers.eventId;
    } catch (error) {
      try {
        this.instrumentation.count(
          "AnalyticsLogger.trackStructuredEventAndReturnId.failed",
          this.statscTags,
        );
        if (error instanceof Error) {
          this.instrumentation.addError(error);
        }
      } catch {
        // Instrumentation must not defeat this API's product-logic isolation.
      }
      return undefined;
    }
  }

  /**
   * Builds a structured payload at the final initialized send seam. Callers
   * that derive payload fields from mutable transport context can use this to
   * keep those fields aligned with the envelope built by sendStructuredEvent.
   */
  async trackStructuredEventWithPayloadFactory<
    M extends MessageFns<any, string>,
    Payload extends MessagePayload<M>,
  >(
    messageType: M,
    buildPayload: () => NoInfer<NoExtraKeys<Payload, MessagePayload<M>>>,
    options?: StructuredEventOptions,
  ) {
    const { trackPromise } = this.scheduleStructuredEvent(messageType, buildPayload, options);
    await trackPromise;
  }

  private trackStructuredTrackPromise(trackPromise: Promise<unknown>): void {
    const trackedPromise = this.settleWithin(trackPromise, MAX_PENDING_STRUCTURED_TRACK_MS);

    this.pendingStructuredTrackPromises.add(trackedPromise);
    void trackedPromise.finally(() => {
      this.pendingStructuredTrackPromises.delete(trackedPromise);
    });
  }

  private async sendStructuredEvent<M extends MessageFns<any, string>>(
    messageType: M,
    payload: MessagePayload<M>,
    identifiers: StructuredEventIdentifiers,
    options?: StructuredEventOptions,
  ) {
    const eventName = this.normalizeEventName(messageType.$type);

    if (this.structuredEventTransport === "statsig") {
      const envelope = this.buildStatsigEnvelope(messageType, payload, identifiers);
      this.analyticsStatsigClient?.validateStructuredEventOptions(options);
      try {
        this.analyticsStatsigClient?.logStructuredEvent(
          PROTOBUF_STRUCTURED_EVENT_NAME,
          envelope,
          options,
        );
      } catch (error) {
        this.instrumentation.count("AnalyticsLogger.statsigLogEvent.failed", this.statscTags);
        if (error instanceof Error) {
          this.instrumentation.addError(error, { eventName });
        }
      }
    } else {
      const envelope = this.buildSegmentEnvelope(messageType, payload, identifiers);
      const trackSegmentPromise = this.analytics
        .then(([analytics]) => analytics.track(PROTOBUF_STRUCTURED_EVENT_NAME, envelope))
        .catch(() => undefined);

      // Keep flush aware of the downstream Segment track promise; the outer
      // init-gated trackStructuredEvent promise resolves before this settles.
      this.trackStructuredTrackPromise(trackSegmentPromise);
      await this.analytics;
    }

    const statscTags = {
      platform: "web",
      event_name: eventName,
      ...this.statscTags,
    };
    this.instrumentation.count("analytics_event_tracked", statscTags);
  }

  private async drainPendingStructuredTracks(deadlineMs: number): Promise<void> {
    while (true) {
      const remainingMs = deadlineMs - Date.now();
      if (remainingMs <= 0) {
        return;
      }

      const pending = Array.from(this.pendingStructuredTrackPromises);
      if (pending.length === 0) {
        return;
      }

      await Promise.race([
        Promise.allSettled(pending).then(() => undefined),
        new Promise<void>((resolve) => {
          setTimeout(resolve, remainingMs);
        }),
      ]);
    }
  }

  async flush(maxWaitMs: number = 1000): Promise<void> {
    const timeoutMs = Number.isFinite(maxWaitMs) && maxWaitMs >= 0 ? maxWaitMs : 1000;
    const deadlineMs = Date.now() + timeoutMs;
    await this.drainPendingStructuredTracks(deadlineMs).catch(() => undefined);

    const remainingMs = deadlineMs - Date.now();
    if (remainingMs > 0 && this.structuredEventTransport === "statsig") {
      const statsigFlushPromise = this.analyticsStatsigClient?.flush();
      if (statsigFlushPromise === undefined) {
        return;
      }
      await Promise.race([
        Promise.resolve(statsigFlushPromise)
          .catch(() => undefined)
          .then(() => undefined),
        new Promise<void>((resolve) => {
          setTimeout(resolve, remainingMs);
        }),
      ]);
    }
  }

  async trackCounter<E extends ProtoEnum>(
    counterName: Extract<E[keyof E], string>,
    counterValue?: number,
    options?: StructuredEventOptions,
  ) {
    return this.trackStructuredEventInternal(
      "ClientEventCounter",
      {
        counterName: counterName,
        metricValue: counterValue,
      },
      options,
    );
  }
}
